import { HttpInterceptorFn, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, switchMap, BehaviorSubject, filter, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../services/token.service';
import { NotificationToastService } from '../services/notification-toast.service';
import { apiErrorMessage } from '../utils/api-error.utils';

// Token to avoid infinite retry loops
export const BYPASS_ERROR_INTERCEPTOR = new HttpContextToken(() => false);

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const toastService = inject(NotificationToastService);

  if (req.context.get(BYPASS_ERROR_INTERCEPTOR)) {
    return next(req);
  }

  const notifyError = (error: HttpErrorResponse): void => {
    toastService.showError(apiErrorMessage(error, 'Une erreur est survenue.'));
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          const refreshToken = tokenService.getRefreshToken();
          if (refreshToken) {
            return authService.refresh(refreshToken).pipe(
              switchMap((res) => {
                isRefreshing = false;
                refreshTokenSubject.next(res.accessToken);
                return next(
                  req.clone({
                    setHeaders: { Authorization: `Bearer ${res.accessToken}` },
                  }),
                );
              }),
              catchError((err) => {
                isRefreshing = false;
                toastService.showError('Session expirée. Veuillez vous reconnecter.');
                authService.logout();
                return throwError(() => err);
              }),
            );
          } else {
            isRefreshing = false;
            toastService.showError('Session expirée. Veuillez vous reconnecter.');
            authService.logout();
          }
        } else {
          return refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => {
              return next(
                req.clone({
                  setHeaders: { Authorization: `Bearer ${token}` },
                }),
              );
            }),
          );
        }
      } else if (error.status === 403) {
        // Retour utilisateur explicite au lieu d'un silence (ex. permission manquante).
        notifyError(error);
      } else if (error.status === 404) {
        toastService.showError(apiErrorMessage(error, 'Ressource introuvable.'));
      } else if (error.status >= 500) {
        toastService.showError(apiErrorMessage(error, 'Erreur serveur. Veuillez réessayer.'));
      } else {
        // Show notification for other errors
        const msg = apiErrorMessage(error, 'Une erreur est survenue.');

        // Exclude validation errors which might not follow ApiError pattern
        // Validation errors usually have status 400
        if (error.status !== 400 && msg) {
          toastService.showError(msg);
        }
      }
      return throwError(() => error);
    }),
  );
};
